import type { ReactNode } from 'react';
import { createContext, useRef, useState, useMemo, useEffect } from 'react';
import { Socket } from 'phoenix';

import { createSuccessNotification, createWarningNotification } from 'app/components/Notification';
import { hasAccessToFeature } from 'app/utils/permissions';

interface Props<T> {
  children: ReactNode;
  url: string;
  params: T;
  userType: UserType;
}

interface RealtimeContextValues {
  socket: Socket | null;
  status: 'open' | 'close';
}

const RealtimeContext = createContext<RealtimeContextValues>({
  socket: null,
  status: 'close'
});

// :
const RealtimeProvider = <T extends object>({ url, params, userType, children }: Props<T>) => {
  const socket = useMemo(() => new Socket(url, { params }), []);
  const initialized = useRef<boolean>(false);

  const [status, setStatus] = useState<'open' | 'close'>('close');

  const handleOpen = () => {
    console.info('Web Socket open');

    // avoid to send notification on initial connection
    if (initialized.current) {
      // send notification only to pandascore traders
      if (hasAccessToFeature('REALTIME_PROVIDER', userType)) {
        createSuccessNotification('common-websocket_connected_notif');
      }
    } else {
      initialized.current = true;
    }

    setStatus('open');
  };

  const handleError = () => {
    console.warn('Web Socket error');
    setStatus('close');
  };

  const handleClose = () => {
    console.warn('Web Socket error');

    // send notification only to pandascore traders
    if (status !== 'close' && hasAccessToFeature('REALTIME_PROVIDER', userType)) {
      createWarningNotification('common-websocket_disconnected_notif');
    }

    setStatus('close');
  };

  useEffect(() => {
    socket.onOpen(handleOpen);
    socket.onError(handleError);
    socket.onClose(handleClose);

    socket.connect();
  }, []);

  const contextValue: RealtimeContextValues = useMemo(
    () => ({
      socket,
      status
    }),
    [status]
  );

  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
};

RealtimeProvider.displayName = 'RealtimeProvider';

export default RealtimeProvider;
export { RealtimeContext };
